import { useState, useEffect, useRef, useCallback } from 'react';
import { Server, Radio, Key } from 'lucide-react';
import type { Town, TownDevice } from '@shared/types/town';
import { DEVICE_TYPES } from '@shared/types/town';

const PHYSICAL_KEY_OPTIONS = [
    'X(미사용)', 'DKM-3015', 'DKC-3100', 'DKC-3101S', 'DKC-3102S',
    'DKM-3100K', 'DKC-3101K', 'HOS-200', 'HOS-200P', 'HOS-200P2', 'HOS-200L',
];
const HOMENET_VENDOR_OPTIONS = [
    '없음', '이지빌', '자이', '코콤', 'Commax', '경동원', '계양정보통신',
    '현대UPIS', '현대통신', 'HDC', '액션테크', '혜인', '경동나비엔',
];
const SURVEILLANCE_VENDOR_OPTIONS = [
    '현대', '네오이엔씨', '나슬시스템', 'NAVI', '한기술',
    '천조', '성빈시스템', '신보파워텍', '코맥스', 'BPN솔루션', 'OTIS',
];

interface DeviceTabProps {
    town: Town;
    devices: TownDevice[];
    onSave: (data: { townFields: Record<string, unknown>; devices: TownDevice[] }) => void;
    isSaving: boolean;
    saveSignal?: number;
}

export default function DeviceTab({ town, devices, onSave, isSaving, saveSignal }: DeviceTabProps) {
    const [serverType, setServerType] = useState(town.server_type ?? '');
    const [serverTypeDetail, setServerTypeDetail] = useState(town.server_type_detail ?? '');
    const [serverVersion, setServerVersion] = useState(town.server_version ?? '');
    const [serverVersionAptset, setServerVersionAptset] = useState(town.server_version_aptset ?? '');
    const [tvboxVersion, setTvboxVersion] = useState(town.tvbox_version ?? '');
    const [guardBoxCount, setGuardBoxCount] = useState(town.guard_box_count ?? 0);
    const [tvboxDetails, setTvboxDetails] = useState<Array<{ no: number; model: string; version: string }>>(
        town.tvbox_details ?? [],
    );
    const [physicalKeyModel, setPhysicalKeyModel] = useState(town.physical_key_model ?? '');
    const [homenetVendor, setHomenetVendor] = useState(town.homenet_vendor ?? '');
    const [surveillanceVendor, setSurveillanceVendor] = useState(town.surveillance_vendor ?? '');
    const [cctvVendor, setCctvVendor] = useState(town.cctv_vendor ?? '');
    const [parkingLocation, setParkingLocation] = useState(Boolean(town.parking_location));
    const [hallCall, setHallCall] = useState(Boolean(town.hall_call));
    const [deviceMap, setDeviceMap] = useState<Record<string, { is_used: boolean; count: number }>>({});

    const isInitialMount = useRef(true);

    useEffect(() => {
        setServerType(town.server_type ?? '');
        setServerTypeDetail(town.server_type_detail ?? '');
        setServerVersion(town.server_version ?? '');
        setServerVersionAptset(town.server_version_aptset ?? '');
        setTvboxVersion(town.tvbox_version ?? '');
        setGuardBoxCount(town.guard_box_count ?? 0);
        setTvboxDetails(town.tvbox_details ?? []);
        setPhysicalKeyModel(town.physical_key_model ?? '');
        setHomenetVendor(town.homenet_vendor ?? '');
        setSurveillanceVendor(town.surveillance_vendor ?? '');
        setCctvVendor(town.cctv_vendor ?? '');
        setParkingLocation(Boolean(town.parking_location));
        setHallCall(Boolean(town.hall_call));
    }, [town]);

    useEffect(() => {
        const map: Record<string, { is_used: boolean; count: number }> = {};
        for (const dt of DEVICE_TYPES) {
            const existing = devices.find((d) => d.device_type === dt);
            map[dt] = { is_used: Boolean(existing?.is_used), count: existing?.count ?? 0 };
        }
        setDeviceMap(map);
    }, [devices]);

    const doSave = useCallback(() => {
        const townFields: Record<string, unknown> = {
            server_type: serverType || null,
            server_type_detail: serverTypeDetail || null,
            server_version: serverVersion || null,
            server_version_aptset: serverVersionAptset || null,
            tvbox_version: tvboxVersion || null,
            tvbox_details: tvboxDetails.length > 0 ? tvboxDetails : null,
            guard_box_count: guardBoxCount,
            physical_key_model: physicalKeyModel || null,
            homenet_vendor: homenetVendor || null,
            surveillance_vendor: surveillanceVendor || null,
            cctv_vendor: cctvVendor || null,
            parking_location: parkingLocation,
            hall_call: hallCall,
        };
        const deviceList: TownDevice[] = DEVICE_TYPES.map((dt) => ({
            town_id: town.id,
            device_type: dt,
            is_used: deviceMap[dt]?.is_used ?? false,
            count: deviceMap[dt]?.count ?? 0,
        }));
        onSave({ townFields, devices: deviceList });
    }, [serverType, serverTypeDetail, serverVersion, serverVersionAptset, tvboxVersion, tvboxDetails, guardBoxCount, physicalKeyModel, homenetVendor, surveillanceVendor, cctvVendor, parkingLocation, hallCall, deviceMap, town.id, onSave]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (saveSignal !== undefined && saveSignal > 0) {
            doSave();
        }
    }, [saveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGuardBoxCountChange = (count: number) => {
        const c = Math.max(0, Math.min(6, count));
        setGuardBoxCount(c);
        setTvboxDetails((prev) => {
            const next: Array<{ no: number; model: string; version: string }> = [];
            for (let i = 1; i <= c; i++) {
                const existing = prev.find((d) => d.no === i);
                next.push(existing ?? { no: i, model: '', version: '' });
            }
            return next;
        });
    };

    const handleTvboxChange = (no: number, field: 'model' | 'version', value: string) => {
        setTvboxDetails((prev) =>
            prev.map((d) => (d.no === no ? { ...d, [field]: value } : d)),
        );
    };

    const toggleDevice = (dt: string) => {
        setDeviceMap((prev) => ({ ...prev, [dt]: { ...prev[dt], is_used: !prev[dt].is_used } }));
    };

    const setDeviceCount = (dt: string, count: number) => {
        setDeviceMap((prev) => ({ ...prev, [dt]: { ...prev[dt], count: Math.max(0, count) } }));
    };

    const inputClass = 'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';
    const selectClass = 'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';
    const labelClass = 'block text-[11px] font-medium text-[#ADB5BD] mb-1';

    return (
        <div>
            {/* 서버 정보 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Server className="w-3.5 h-3.5 text-[#1ABB9C]" /> 서버 정보
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>서버 타입</label>
                        <select value={serverType} onChange={(e) => setServerType(e.target.value)} className={selectClass}>
                            <option value="">선택</option>
                            <option value="RACK">RACK</option>
                            <option value="데스크탑">데스크탑</option>
                            <option value="미니PC">미니PC</option>
                            <option value="클라우드">클라우드</option>
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>모델명</label>
                        <input
                            type="text"
                            value={serverTypeDetail}
                            onChange={(e) => setServerTypeDetail(e.target.value)}
                            placeholder={serverType === '데스크탑' ? '모델명 입력' : ''}
                            className={inputClass}
                            disabled={serverType !== '데스크탑' && serverType !== '미니PC'}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>서버 버전 (aptmon)</label>
                        <input type="text" value={serverVersion} onChange={(e) => setServerVersion(e.target.value)} placeholder="v0.0.0" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>서버 버전 (aptset)</label>
                        <input type="text" value={serverVersionAptset} onChange={(e) => setServerVersionAptset(e.target.value)} placeholder="v0.0.0" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>경비실기(TV박스) 갯수 (최대 6)</label>
                        <input type="number" value={guardBoxCount} onChange={(e) => handleGuardBoxCountChange(parseInt(e.target.value) || 0)} min={0} max={6} className={inputClass} />
                    </div>
                </div>

                {/* TV박스 상세 — 갯수만큼 행 표시 */}
                {tvboxDetails.length > 0 && (
                    <div className="mt-4 space-y-2">
                        {tvboxDetails.map((box) => (
                            <div key={box.no} className="flex items-center gap-3 px-3 py-2 bg-[#F5F7FA] rounded-lg border border-[#E6E9ED]">
                                <span className="text-xs font-semibold text-[#73879C] w-16">TV박스 #{box.no}</span>
                                <select
                                    value={box.model}
                                    onChange={(e) => handleTvboxChange(box.no, 'model', e.target.value)}
                                    className="px-2 py-1.5 text-sm border border-[#E6E9ED] rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-[#1ABB9C] w-24"
                                >
                                    <option value="">모델</option>
                                    <option value="009">009</option>
                                    <option value="011">011</option>
                                </select>
                                <input
                                    type="text"
                                    value={box.version}
                                    onChange={(e) => handleTvboxChange(box.no, 'version', e.target.value)}
                                    placeholder="버전 (v0.0.0)"
                                    className="flex-1 px-2 py-1.5 text-sm border border-[#E6E9ED] rounded-md focus:outline-none focus:ring-1 focus:ring-[#1ABB9C]"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 디바이스 사용 현황 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Radio className="w-3.5 h-3.5 text-[#1ABB9C]" /> 디바이스 사용 현황
                </h3>
                <p className="text-[11px] text-[#ADB5BD] mb-3">사용하는 디바이스를 체크하고 갯수를 입력하세요</p>
                <div className="grid grid-cols-2 gap-2.5">
                    {DEVICE_TYPES.map((dt) => {
                        const checked = deviceMap[dt]?.is_used ?? false;
                        return (
                            <label
                                key={dt}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition ${checked ? 'bg-[#1ABB9C]/10 border-[#1ABB9C]' : 'border-[#E6E9ED]'
                                    }`}
                            >
                                <input type="checkbox" checked={checked} onChange={() => toggleDevice(dt)} className="accent-blue-500 w-4 h-4 rounded" />
                                <span className="text-sm flex-1">{dt}</span>
                                <input
                                    type="number"
                                    value={deviceMap[dt]?.count ?? 0}
                                    onChange={(e) => setDeviceCount(dt, parseInt(e.target.value) || 0)}
                                    min={0}
                                    className="w-14 px-2 py-1 text-xs text-center border border-[#E6E9ED] rounded-md focus:outline-none focus:ring-1 focus:ring-[#1ABB9C]"
                                />
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* 실물키 · 기타 */}
            <div>
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Key className="w-3.5 h-3.5 text-[#1ABB9C]" /> 실물키 · 기타
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>실물키 사용</label>
                        <select value={physicalKeyModel} onChange={(e) => setPhysicalKeyModel(e.target.value)} className={selectClass}>
                            <option value="">선택</option>
                            {PHYSICAL_KEY_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>홈넷 업체</label>
                        <select value={homenetVendor} onChange={(e) => setHomenetVendor(e.target.value)} className={selectClass}>
                            <option value="">선택</option>
                            {HOMENET_VENDOR_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>감시반 업체</label>
                        <select value={surveillanceVendor} onChange={(e) => setSurveillanceVendor(e.target.value)} className={selectClass}>
                            <option value="">선택</option>
                            {SURVEILLANCE_VENDOR_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>CCTV 업체</label>
                        <input type="text" value={cctvVendor} onChange={(e) => setCctvVendor(e.target.value)} placeholder="업체명 입력" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>주차위치 사용</label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={parkingLocation} onChange={(e) => setParkingLocation(e.target.checked)} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#1ABB9C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1ABB9C]" />
                            </label>
                            <span className="text-xs text-[#73879C]">{parkingLocation ? '사용' : '미사용'}</span>
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>홀콜/목적층</label>
                        <div className="flex items-center gap-2 mt-1.5">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" checked={hallCall} onChange={(e) => setHallCall(e.target.checked)} className="sr-only peer" />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#1ABB9C]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#1ABB9C]" />
                            </label>
                            <span className="text-xs text-[#73879C]">{hallCall ? '사용' : '미사용'}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
